import React, { useState, useEffect } from 'react';

/**
 * KinFinancialStatement
 *
 * Role-aware financial statement for a Kin.
 * Members see aggregate figures and a de-identified transaction list.
 * The KC additionally sees itemised member contributions.
 *
 * Data sourced from the payments ledger (PaymentAccount + FundsIn).
 */
export default function KinFinancialStatement({ kinId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (kinId) fetchStatement();
  }, [kinId, period]);

  const fetchStatement = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/kin/financial-statement/${kinId}?period=${period}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      console.error('Error fetching financial statement:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading financial statement...</div>;
  }
  if (error) {
    return <div className="text-center py-8 text-red-600">Error: {error}</div>;
  }
  if (!data) {
    return <div className="text-center py-8">No data available</div>;
  }

  const isKC = data.role === 'kc';
  const summary = data.summary || {};
  const transactions = data.transactions || [];
  const members = data.members || [];

  const hasActivity = (summary.totalIn || 0) > 0 || (summary.totalOut || 0) > 0;

  const formatMoney = (n) =>
    '$' + (Number(n) || 0).toFixed(2);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }) : '—';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="text-2xl font-bold">📊 Kin Financial Statement</h2>
        <div className="flex gap-2">
          {[
            { id: 'week', label: '7 Days' },
            { id: 'month', label: '30 Days' },
            { id: 'quarter', label: '90 Days' },
            { id: 'year', label: '1 Year' },
          ].map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded text-sm ${
                period === p.id
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Money In</div>
          <div className="text-2xl font-bold text-green-600">
            {formatMoney(summary.totalIn)}
          </div>
          {!hasActivity && (
            <div className="text-xs text-gray-400 mt-1">No activity this period</div>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Money Out</div>
          <div className="text-2xl font-bold text-red-600">
            {formatMoney(summary.totalOut)}
          </div>
          {!hasActivity && (
            <div className="text-xs text-gray-400 mt-1">No payments yet</div>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Available for Orders</div>
          <div className="text-2xl font-bold text-green-700">
            {formatMoney(summary.availableForOrders)}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            Excludes security deposit
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Kin Balance</div>
          <div className="text-2xl font-bold text-blue-700">
            {formatMoney(summary.currentBalance)}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            Held in the Kin account
          </div>
        </div>
      </div>

      {/* Security deposit */}
      {summary.securityDeposit > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🔒</span>
            <div className="flex-1">
              <div className="font-medium text-amber-900">
                Security Deposit: {formatMoney(summary.securityDeposit)}
              </div>
              <p className="text-sm text-amber-800 mt-1">
                Held in reserve by the Kin Coordinator to cover shortfalls and
                enable larger orders. Returned in full if the Coordinator
                leaves their role.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KC-only: Member contributions table */}
      {isKC && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <h3 className="text-lg font-semibold p-4 border-b">
            Member Contributions (this period)
          </h3>
          {members.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No members have contributed yet.</p>
              <p className="text-sm mt-1">
                Members' contributions will appear here once they pledge.
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Member
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Contributions
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Security Deposit
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Available
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {members.map((m) => (
                  <tr key={m.memberId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{m.name}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">
                      {formatMoney(m.contributions)}
                    </td>
                    <td className="px-4 py-3 text-right text-amber-700">
                      {m.securityDeposit > 0 ? formatMoney(m.securityDeposit) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {formatMoney(m.available)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Transactions */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <h3 className="text-lg font-semibold p-4 border-b">
          Transactions{isKC ? '' : ' (this period)'}
        </h3>
        {transactions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No transactions this period.</p>
            <p className="text-sm mt-1">
              Contributions and payments will appear here.
            </p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Description
                  </th>
                  {isKC && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Member
                    </th>
                  )}
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.map((tx, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm whitespace-nowrap">
                      {formatDate(tx.date)}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      {tx.description}
                      {tx.note && (
                        <div className="text-xs text-gray-500 italic mt-0.5">
                          {tx.note}
                        </div>
                      )}
                    </td>
                    {isKC && (
                      <td className="px-4 py-2 text-sm">
                        {tx.memberName || '—'}
                      </td>
                    )}
                    <td className="px-4 py-2 text-right text-sm font-medium text-green-700 whitespace-nowrap">
                      +{formatMoney(tx.amount)}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          tx.type === 'contribution'
                            ? 'bg-green-100 text-green-800'
                            : tx.type === 'security_deposit'
                            ? 'bg-amber-100 text-amber-800'
                            : tx.type === 'payment_out'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {tx.type.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transparency note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <div className="flex items-start gap-2">
          <span>🔍</span>
          <div>
            <div className="font-medium">Transparency</div>
            <p className="mt-1">
              Every member of this Kin sees the same financial statement.
              All contributions, payments, and fees are visible to everyone
              in the Kin, and are backed by the platform's audit trail.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
